import { createColumnHelper } from "@tanstack/react-table";
import { format } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Label } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { usePermanentLocations, usePermanentRealtimeDealers } from "components/DealersAndLocationsDropdown/hooks";
import { useNewsFilters } from "modules/NewsDashboard/components/NewsDashboardFilters/hooks";
import { useNewsDashboardTableHandlers } from "modules/NewsDashboard/components/NewsDashboardTable/hooks/useNewsDashboardTableHandlers";
import "modules/NewsDashboard/components/NewsDashboardTable/NewsDashboardTable.scss";
import { useLanguageOptions, useNewsCategoriesQuery } from "modules/NewsDashboard/hooks";

import type { Article } from "models";

type NewsDashboardTableProps = {
  isLoading: boolean;
  articles?: Article[];
  nb_pages?: number;
  nb_items?: number;
  onOpenModal: (article: Article) => void;
  onRowClick?: (article: Article) => void;
};

const columnHelper = createColumnHelper<Article>();

export const NewsDashboardTable = ({ onOpenModal, onRowClick, articles, nb_items = 0, nb_pages = 0, isLoading }: NewsDashboardTableProps) => {
  const { t } = useTranslation();

  const { locations } = usePermanentLocations();
  const { data: categories } = useNewsCategoriesQuery();
  const { dealers } = usePermanentRealtimeDealers();
  const { getLanguageDisplayText } = useLanguageOptions();
  const { handlePageSizeChange, handlePaginationChange } = useNewsDashboardTableHandlers();

  const { page: currentPage, page_size: pageSize } = useNewsFilters();

  const getDealers = (dealerIds?: number[]) => {
    if (!dealerIds?.length) return null;

    const dealerNames = dealerIds.map(id => dealers.find(d => d.id === id)?.name).filter(Boolean);
    return dealerNames.join(", ");
  };

  const getLocations = (locationIds?: number[]) => {
    if (!locationIds?.length) return null;

    const locationNames = locationIds.map(id => locations.find(location => location.id === id)?.name).filter(Boolean);
    return locationNames.join(", ");
  };

  const handleOnEditButtonClick = (e: React.MouseEvent<HTMLButtonElement>, article: Article) => {
    e.stopPropagation();
    onOpenModal(article);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "index",
        header: () => <span className="row-index"> # </span>,
        size: 30,
        cell: ({
          row: {
            original: { disabled },
            index: idx
          }
        }) => {
          const rowIdx = idx + 1;
          const articleNumberOnPage = currentPage > 1 ? (currentPage - 1) * pageSize + rowIdx : rowIdx;

          return (
            <div className={`article-container ${disabled ? "disabled-article" : "active-article"}`}>
              <span className={disabled ? "disabled-article-text" : "active-article-text"}>{articleNumberOnPage}</span>
            </div>
          );
        }
      }),

      columnHelper.accessor(row => row, {
        id: "title",
        header: () => <span className="header-title"> {t("v8_title", "Title")} </span>,
        size: 200,
        cell: ({
          row: {
            original: { title }
          }
        }) => (
          <div className="title-container">
            <span className="text-ellipsis">{title}</span>
          </div>
        )
      }),

      columnHelper.accessor(row => row, {
        id: "dealers",
        header: () => <span className="header-title"> {t("v8_dealer", "Dealer")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { visible_to_all, dealer_ids }
          }
        }) => {
          if (visible_to_all) return <span>{t("v8_all_dealers", "All dealers")}</span>;

          return <span className="text-ellipsis">{getDealers(dealer_ids)}</span>;
        }
      }),

      columnHelper.accessor(row => row, {
        id: "locations",
        header: () => <span className="header-title"> {t("v8_location", "Location")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { visible_to_all, location_ids }
          }
        }) => {
          if (visible_to_all) return <span>{t("v8_all_locations", "All locations")}</span>;

          return <span className="text-ellipsis">{getLocations(location_ids)}</span>;
        }
      }),

      columnHelper.accessor(row => row, {
        id: "publish_date",
        header: () => <span className="header-title"> {t("v8_publish_date", "Publish Date")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { publish_date }
          }
        }) => <span className="text">{publish_date ? format(new Date(publish_date), DATE_FORMATS_FNS.dateMonthYear) : ""}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "expire_date",
        header: () => <span className="header-title"> {t("v8_expiry_date", "Expiry Date")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { expire_date }
          }
        }) => <span className="text">{expire_date ? format(new Date(expire_date), DATE_FORMATS_FNS.dateMonthYear) : ""}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "category",
        header: () => <span className="header-title"> {t("v8_category", "Category")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { category_id }
          }
        }) => {
          if (!category_id || !categories) return;

          const category = categories.find(cat => cat.id === category_id);
          return <Label className="category-label">{t(`v8_${category?.name}`) || category?.name || ""}</Label>;
        }
      }),

      columnHelper.accessor(row => row, {
        id: "language",
        header: () => <span className="header-title"> {t("v8_language", "Language")} </span>,
        size: 100,
        cell: ({
          row: {
            original: { language_code }
          }
        }) => <span className="text">{getLanguageDisplayText(language_code)}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "published",
        header: () => <span className="header-title"> {t("v8_published", "Published")} </span>,
        size: 120,
        cell: ({
          row: {
            original: { user }
          }
        }) => (user ? <span className="text">{`${user.first_name} ${user.last_name}`}</span> : "")
      }),

      columnHelper.accessor(row => row, {
        id: "stripe_opened_count",
        header: () => <span className="header-title text-center"> {t("v8_stripe_opened_count", "Stripe Opened Count")} </span>,
        size: 120,
        cell: ({
          row: {
            original: { stripe_opened_count }
          }
        }) => <span className="text-center">{stripe_opened_count}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "stripe_closed_count",
        header: () => <span className="header-title text-center"> {t("v8_stripe_closed_count", "Stripe Closed Count")} </span>,
        size: 120,
        cell: ({
          row: {
            original: { stripe_closed_count }
          }
        }) => <span className="text-center">{stripe_closed_count}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "actions",
        header: "",
        size: 80,
        cell: ({ row: { original: article } }) => (
          <Button icon onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleOnEditButtonClick(e, article)} className="edit-button">
            <Icon className="pen to square" />
          </Button>
        )
      })
    ],
    [t, getDealers, getLocations, getLanguageDisplayText, categories, currentPage, pageSize]
  );

  return (
    <div className="NewsDashboardTable">
      <ReactTable
        isFetching={isLoading}
        columns={columns}
        data={articles}
        pageCount={nb_pages}
        pageSize={pageSize}
        totalItems={nb_items}
        pagination={nb_pages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined}
        onPaginationChange={handlePaginationChange}
        onChangePageSize={handlePageSizeChange}
        onRowClick={(article: Article) => onRowClick?.(article)}
      />
    </div>
  );
};
